import { Button, Col, Form, Row, Spin } from 'antd';
import { FormAttachment } from '../../../components/form/FormBasic';
import { FileTypeEnum } from '../../../enums/FileTypeEnum';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useWatch } from 'antd/es/form/Form';
import { Download } from 'react-bootstrap-icons';
import { UploadResultSummary } from '../../analysisAndReports/components/UploadResultSummary';
import { useMutation } from '@tanstack/react-query';
import { downloadProductTemplate, uploadProductBulk } from './getData';

const BulkProductUploadView: React.FC = () => {
  const { t } = useTranslation(['productAndBasketManagement']);
  const [productUploadForm] = Form.useForm();
  const summaryRef = useRef<HTMLDivElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (data: any) => uploadProductBulk(data),
  });

  const downloadTemplateMutation = useMutation({
    mutationFn: () => downloadProductTemplate(),
  });

  const { successNotification, errorNotification } = useNotificationContext();

  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const attachmentValue = useWatch('fileContent', productUploadForm);

  const hasAllValues = useMemo(() => {
    return attachmentValue && attachmentValue?.length > 0;
  }, [attachmentValue]);

  useEffect(() => {
    if (uploadResponse && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [uploadResponse]);

  const handleFormChanges = () => {
    if (uploadResponse) {
      setUploadResponse(null);
    }
  };

  const handleSubmit = (values: any) => {
    const fileBase64 = values.fileContent?.[0]?.image;

    if (!fileBase64) {
      return;
    }

    setUploadResponse(null);

    uploadMutation.mutate(
      {
        fileData: fileBase64,
      },
      {
        onSuccess: (response: any) => {
          // Extract response data - handle nested structure
          const responseData = response?.data?.data || response?.data || response;

          setUploadResponse(responseData);

          // Check if it's a validation error response
          if (responseData?.sheets && responseData?.summary) {
            if (responseData?.isValid) {
              successNotification(t('uploadSuccess'));
            } else {
              errorNotification(t('templateValidationFailed'));
            }
          } else {
            // It's a successful upload response
            const successMessage = response?.data?.message || t('uploadSuccess');
            successNotification(successMessage);
            productUploadForm.resetFields();
          }
        },
        onError: (error: any) => {
          const errorData = error?.response?.data;
          setUploadResponse(errorData);
          errorNotification(errorData?.message || error?.message);
          productUploadForm.resetFields();
        },
      }
    );
  };

  const handleDownloadTemplate = () => {
    downloadTemplateMutation.mutate(undefined, {
      onSuccess: (response: any) => {
        const blob = response instanceof Blob ? response : response?.data || response;

        if (!(blob instanceof Blob)) {
          errorNotification(t('templateDownloadError'));
          return;
        }

        let fileName = 'product-template.xlsx';
        const contentDisposition = response?.response?.headers?.['content-disposition'];

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        successNotification(t('templateDownloadReady'));
      },
      onError: (_error: any) => {
        errorNotification(t('templateDownloadError'));
      },
    });
  };

  return (
    <div className="mb-6">
      <div className="mt-4 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h2 className="text-xl font-medium text-headings-font-color">
            {t('bulkProductUploadTitle')}
          </h2>
          <p className="text-base font-medium text-font-color">
            {t('bulkProductUploadDescription')}
          </p>
        </div>
      </div>

      <Form
        form={productUploadForm}
        onFinish={handleSubmit}
        layout="vertical"
        className="mt-3 w-full"
        onValuesChange={handleFormChanges}
      >
        <Row className="mb-4">
          <Col span={24}>
            <Button
              type="link"
              size="small"
              icon={<Download size={16} />}
              onClick={handleDownloadTemplate}
              loading={downloadTemplateMutation.isPending}
              className="p-0"
            >
              {t('downloadTemplate')}
            </Button>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FormAttachment
              name={'fileContent'}
              label={t('uploadAttachment')}
              form={productUploadForm}
              isRequired
              validationMessage={t('attachmentRequired')}
              maxCount={1}
              fileTypes={[FileTypeEnum.EXCEL]}
              initialFiles={attachmentValue ? attachmentValue : []}
            />
          </Col>
        </Row>

        <Row className="mt-6 justify-end gap-4">
          <Form.Item className="flex justify-end">
            <PrimarySquareBtn
              handleOnClick={() => {}}
              disabled={uploadMutation.isPending || !hasAllValues}
            >
              <div className="flex">
                {/* <Upload className="h-4 w-6 text-white" /> */}
                <p className="ml-1 pt-0">{t('upload')}</p>
              </div>
            </PrimarySquareBtn>
          </Form.Item>
        </Row>
      </Form>

      <div ref={summaryRef} className="mb-6">
        <Spin spinning={uploadMutation.isPending} size="large">
          {uploadResponse && <UploadResultSummary response={uploadResponse} />}
        </Spin>
      </div>
    </div>
  );
};

export default BulkProductUploadView;
